<script>
  import { getContext } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link } from 'svelte-navigator';
  import {
    Breadcrumb,
    BreadcrumbItem,
    Button,
    ButtonDropdown,
    Col,
    Container,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Row
  } from '@sveltestrap/sveltestrap';
  import { format as formatDate, parseISO } from 'date-fns';
  import { unparse } from 'papaparse';

  import { SearchFilter } from '@client/components/filters/search';
  import { Loader } from '@client/components/loader';
  import { getNotificationsContext } from '@client/components/notifications';
  import { Spinner } from '@client/components/spinner';
  import { Table } from '@client/components/table';

  import { DomainService } from '@client/services/domain';

  import { downloadFile } from '@client/utils/download';
  import { calculateUsage } from '@client/utils/usage';

  import { deepCopyArray } from '@common/utils/arrays';

  import { MarketingDelta, SignatureDelta, TrackingDelta } from './cell-formatters';

  const { addNotification } = getNotificationsContext();
  const { basepath } = getContext('config');

  let fetching = false;
  let loading = false;
  let lookback = 60;
  let originalRaw = {};
  let originalData = [];
  let data = [];
  let search = '';

  const EXPORT_DATE_FORMAT = 'M-dd-yyyy-hh-mm-ss';
  const EXPORT_TYPE = 'text/csv;charset=utf-8';

  const notify = (text, error) => {
    addNotification({
      text,
      type: error ? 'error' : 'success',
      autohide: 2500,
      position: 'top-right'
    });
  };

  const createTableData = (items) => {
    return Object.keys(items.cslist).map((item) => items.cslist[item]);
  };

  const getEBCData = async () => {
    loading = fetching = true;

    const response = await DomainService.getEBC();

    if (response?.status === 500) {
      notify($format('label.FAILURE.GET_EBC_DATA'), true);
      loading = fetching = false;
      return;
    }

    originalRaw = response;
    originalData = createTableData(response);
    data = deepCopyArray(originalData);

    fetching = loading = false;

    return response;
  };

  const calculateUsageHelper = (item, domain) => {
    const calucatedUsage = calculateUsage(item);
    const changes = originalRaw?.billingidSeats[lookback][item.billingid];
    const lastLogin =
      domain && changes?.lastLogin[domain].length
        ? `${changes.lastLogin[domain][0]?.user}|${changes.lastLogin[domain][0]?.timestamp}`
        : '';
    const lastActivity =
      domain && changes?.lastUpdate[domain]
        ? `${changes.lastUpdate[domain][0]?.user}|${changes.lastUpdate[domain][0]?.timestamp}|${changes.lastUpdate[domain][0]?.type}`
        : '';

    return {
      signature_used: calucatedUsage.signature.usage.toLocaleString(),
      signature_users_sold: calucatedUsage.signature.sold.toLocaleString(),
      signature_overage: calucatedUsage.signature.overage.toLocaleString(),
      signature_underage: calucatedUsage.signature.underage.toLocaleString(),

      pipeline_used: calucatedUsage.pipeline.usage.toLocaleString(),
      pipeline_users_sold: calucatedUsage.pipeline.sold.toLocaleString(),
      pipeline_overage: calucatedUsage.pipeline.overage.toLocaleString(),
      pipeline_underage: calucatedUsage.pipeline.underage.toLocaleString(),

      complete_used: calucatedUsage.complete.usage.toLocaleString(),
      complete_users_sold: calucatedUsage.complete.sold.toLocaleString(),
      complete_overage: calucatedUsage.complete.overage.toLocaleString(),
      complete_underage: calucatedUsage.complete.underage.toLocaleString(),

      engagement_used: item.tracking_used || item.engagement,
      engagement_users_sold: item.tracking_used + item.tracking_delta || item.engagement_users_sold,

      total_underage: calucatedUsage.totals.totalUnderage.toLocaleString(),
      total_sold: calucatedUsage.totals.totalSold.toLocaleString(),
      total_usage: calucatedUsage.totals.totalUsage.toLocaleString(),
      total_usage_percent: calucatedUsage.totals.totalUsagePercentage.toLocaleString(),
      total_overage: calucatedUsage.totals.totalOverage.toLocaleString(),
      total_overage_percent: calucatedUsage.totals.totalOveragePercentage.toLocaleString(),

      total_signature_changes: item.tagblockchanges,
      total_ad_changes: item.adchanges,

      last_user_login: lastLogin,
      last_user_activity: lastActivity
    };
  };

  const exportCSData = () => {
    const items = originalData.map((item) => {
      const calucatedUsage = calculateUsageHelper(item);

      return {
        name: item.name,
        crm_id: item.crm_id,
        billingid: item.billingid,
        ...calucatedUsage,
        domains: item.domains,
        domaincount: item.domaincount,
        report_date: item.report_date
      };
    });

    const text = unparse(items);
    const blob = new Blob([text], { type: EXPORT_TYPE });
    const today = formatDate(new Date(), EXPORT_DATE_FORMAT);

    downloadFile(blob, `ebc-complete-${today}.csv`);
  };

  const exportOveragesData = () => {
    const items = Object.keys(originalRaw.overagelist).map((item) => originalRaw.overagelist[item]);
    const text = unparse(items);
    const blob = new Blob([text], { type: EXPORT_TYPE });
    const today = formatDate(new Date(), EXPORT_DATE_FORMAT);

    downloadFile(blob, `ebc-overages-${today}.csv`);
  };

  const exportDomainsData = () => {
    const items = Object.keys(originalRaw.domains).map((item) => {
      const domain = originalRaw.domains[item][lookback];
      const calucatedUsage = calculateUsageHelper(domain, item);

      return {
        domain: item,
        billingid: domain.billingid,
        crm_record_count: domain.companyCount,
        ...calucatedUsage,
        total_raw_discount: domain.total_raw_discount
      };
    });

    const text = unparse(items);
    const blob = new Blob([text], { type: EXPORT_TYPE });
    const today = formatDate(new Date(), EXPORT_DATE_FORMAT);

    downloadFile(blob, `ebc-domains-${today}.csv`);
  };

  const exportCSPData = () => {
    const items = Object.keys(originalRaw.domains)
      .map((item) => {
        const domain = originalRaw.domains[item];
        const config = domain[lookback];
        const calucatedUsage = calculateUsageHelper(config, item);

        if (config.billingid && config.billingid !== '0') {
          return {
            'event.time': config.reportDate,
            'org.id': config.billingid,
            'org.name': item,
            'user.id': '',
            'user.name': '',
            'user.email': '',
            ...calucatedUsage,
            last_login_count: calucatedUsage.last_user_login ? 1 : 0,
            last_activity_count: calucatedUsage.last_user_activity ? 1 : 0
          };
        }

        return false;
      })
      .filter(Boolean);

    const text = unparse(items);
    const blob = new Blob([text], { type: EXPORT_TYPE });
    const today = formatDate(new Date(), EXPORT_DATE_FORMAT);

    downloadFile(blob, `ebc-csp-${today}.csv`);
  };

  let columns = [
    {
      key: 'name',
      title: $format('label.NAME'),
      tag: 'th',
      cellClass: 'freeze',
      value: ({ name, domains }) => name || domains.split(',')[0],
      sortable: true
    },
    // {
    //   key: 'crm_id',
    //   title: 'CRM ID',
    //   tag: 'th',
    //   cellClass: 'freeze',
    //   value: ({ crm_id }) => crm_id,
    //   sortable: true
    // },
    {
      key: 'billingid',
      title: $format('label.BILLING_ID'),
      headerClass: 'billingid-column',
      value: ({ billingid }) => billingid,
      sortable: true
    },
    {
      key: 'domaincount',
      title: $format('label.DOMAIN_COUNT'),
      value: ({ domaincount }) => domaincount.toLocaleString(),
      sortable: true
    },
    {
      key: 'domains',
      title: $format('label.DOMAIN_NAMES'),
      value: ({ domains }) =>
        `${domains
          .split(',')
          .map((url) => `<a href="https://${url}" target="_blank">${url}</a>`)
          .join('<br>')}`,
      sortable: true
    },
    {
      key: 'signature_used',
      title: $format('label.SIGNATURE_USED'),
      value: ({ signature_used }) => signature_used.toLocaleString(),
      sortable: true
    },
    {
      key: 'signature_users_sold',
      title: $format('label.SIGNATURE_SOLD'),
      value: ({ signature_users_sold }) => signature_users_sold.toLocaleString(),
      sortable: true
    },
    {
      key: 'signature_delta',
      title: $format('label.SIGNATURE_DELTA'),
      value: ({ signature_delta }) => signature_delta.toLocaleString(),
      renderComponent: SignatureDelta,
      sortable: true
    },
    {
      key: 'marketing_used',
      title: $format('label.MARKETING_USED'),
      value: ({ marketing_used }) => marketing_used.toLocaleString(),
      sortable: true
    },
    {
      key: 'marketing_sold',
      title: $format('label.MARKETING_SOLD'),
      value: ({ marketing_sold }) => marketing_sold.toLocaleString(),
      sortable: true
    },
    {
      key: 'marketing_delta',
      title: $format('label.MARKETING_DELTA'),
      value: ({ marketing_delta }) => marketing_delta.toLocaleString(),
      renderComponent: MarketingDelta,
      sortable: true
    },
    {
      key: 'pipeline_used',
      title: $format('label.PIPELINE_USED'),
      value: ({ pipeline_used }) => pipeline_used.toLocaleString(),
      sortable: true
    },
    {
      key: 'pipeline_users_sold',
      title: $format('label.PIPELINE_SOLD'),
      value: ({ pipeline_users_sold }) => pipeline_users_sold.toLocaleString(),
      sortable: true
    },
    {
      key: 'complete_used',
      title: $format('label.COMPLETE_USED'),
      value: ({ complete_used }) => complete_used.toLocaleString(),
      sortable: true
    },
    {
      key: 'complete_users_sold',
      title: $format('label.COMPLETE_SOLD'),
      value: ({ complete_users_sold }) => complete_users_sold.toLocaleString(),
      sortable: true
    },
    {
      key: 'percent_sold_users_active',
      title: $format('label.PERCENT_ACTIVE_USERS_SOLD'),
      value: ({ percent_sold_users_active }) => percent_sold_users_active.toLocaleString(),
      sortable: true
    },
    {
      key: 'tracking_used',
      title: $format('label.TRACKING_USED'),
      value: ({ tracking_used }) => tracking_used.toLocaleString(),
      sortable: true
    },
    {
      key: 'tracking_delta',
      title: $format('label.TRACKING_DELTA'),
      value: ({ tracking_delta }) => tracking_delta.toLocaleString(),
      renderComponent: TrackingDelta,
      sortable: true
    },
    {
      key: 'total_used',
      title: $format('label.TOTAL_USED'),
      value: ({ total_used }) => total_used.toLocaleString(),
      sortable: true
    },
    // {
    //   key: 'total_sold',
    //   title: 'Total Sold',
    //   value: ({ total_sold }) => total_sold,
    //   sortable: true
    // },
    // {
    //   key: 'total_delta',
    //   title: 'Total Delta',
    //   value: ({ total_delta }) => total_delta,
    //   sortable: true
    // },
    {
      key: 'tagblockchanges',
      title: $format('label.SIGNATURE_UPDATES'),
      value: ({ tagblockchanges }) => tagblockchanges,
      sortable: true
    },
    {
      key: 'adchanges',
      title: $format('label.AD_UPDATES'),
      value: ({ adchanges }) => adchanges,
      sortable: true
    }
    // {
    //   key: 'report_date',
    //   title: 'Report Date',
    //   value: ({ report_date }) => formatDate(parseISO(report_date), 'M/dd/yyyy h:mm:ss a'),
    //   sortable: true
    // },
  ];

  const onSearchFilterChange = () => {
    if (search && originalData.length) {
      data = originalData.filter((item) => {
        return JSON.stringify(item).toLowerCase().indexOf(search.toLowerCase()) > -1;
      });
    } else {
      data = originalData;
    }
  };
</script>

<div id="ebc" class="page">
  <Loader backdrop show={loading} />

  <div class="page-header">
    <Container fluid>
      <Row>
        <Col md="12">
          <Breadcrumb>
            <BreadcrumbItem>
              <a href="{basepath}/staff" use:link>{$format('label.STAFF_TOOLS')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem active>{$format('label.EBC_REPORT')}</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row class="flex-align--center">
        <Col xs="8" sm="6" md="7">
          <h1 class="page-title">{$format('label.EBC_REPORT')}</h1>
        </Col>
        <Col xs="4" sm="6" md="5" style="align-items: center; display: flex; gap: 8px; justify-content: flex-end;">
          {#if originalData.length}
            <small style="color: var(--color-gray-600); margin-right: 8px;">
              {$format('label.EBC_REPORT_AS_OF_DATE', {
                values: { date: formatDate(parseISO(originalData[0].report_date), 'M/dd/yyyy h:mm:ss a') }
              })}
            </small>
          {/if}

          {#if originalData.length}
            <ButtonDropdown class="hide--mobile me-2">
              <DropdownToggle color="secondary" caret>{$format('label.EXPORT')}</DropdownToggle>
              <DropdownMenu>
                <DropdownItem on:click={exportDomainsData}>{$format('label.DOMAINS')}</DropdownItem>
                <DropdownItem on:click={exportOveragesData}>{$format('label.OVERAGES')}</DropdownItem>
                <DropdownItem divider />
                <DropdownItem on:click={exportCSPData}>{$format('label.CSP_FORMAT')}</DropdownItem>
                <DropdownItem divider />
                <DropdownItem on:click={exportCSData}>{$format('label.COMPLETE_DATA')}</DropdownItem>
              </DropdownMenu>
            </ButtonDropdown>
          {/if}

          <Button color="primary" on:click={getEBCData}>
            {#if fetching}
              <Spinner show inline styles="border-width: 2px; color: #0087ff; height: 20px; width: 20px;" />
            {:else if originalData.length}
              {$format('label.REFRESH')}
            {:else}
              {$format('label.REQUEST')}
            {/if}
          </Button>
        </Col>
      </Row>
    </Container>
  </div>
  <div class="page-content">
    <div class="filters">
      <SearchFilter bind:search onChange={onSearchFilterChange} />
    </div>
    {#if originalData.length}
      <div class="scrollable-table">
        <Table
          id="ebc-records"
          classNameThead="sticky-header"
          classNameTable="ebc-records"
          bind:rows={data}
          bind:columns
          sortBy="billingid"
          sortOrder={-1}
        />
      </div>
    {:else}
      <div class="empty-state">
        <div>
          <p>{@html $format('label.GET_EBC_REPORT')}</p>
          <p>{$format('label.DESCRIPTION.GET_EBC_REPORT')}</p>
        </div>
      </div>
    {/if}
  </div>
</div>

<style lang="scss" src="./ebc.scss"></style>
